// ─────────────────────────────────────────────────────────────
//  Live Scoring – sdílené typy pro celý modul závodů
// ─────────────────────────────────────────────────────────────

// ── Terče ─────────────────────────────────────────────────────

export interface Target {
  id: string;
  name: string;
  /** Bodové zóny – může být kladné i záporné číslo. 30.1 = srdeční zásah */
  zones: number[];
  description?: string;
}

// ── Stanoviště ────────────────────────────────────────────────

export type StationType = "target" | "gongs_sequential" | "gongs_choice";

export interface GongConfig {
  seq: number;       // pořadí
  value: number;     // bodová hodnota
  label?: string;    // volitelný popis, např. "Srnec 50m"
}

export interface StationConfigTarget {
  targetId: string;
  targetName: string; // snapshot pro případ změny terče
}

export interface StationConfigGongsSequential {
  gongs: GongConfig[];
}

export interface StationConfigGongsChoice {
  gongs: GongConfig[]; // typicky 3 gongy různých hodnot
}

export interface LiveStation {
  id: string;
  number: number;
  name: string;
  type: StationType;
  shotsCount: number;         // počet ran na stanovišti (default 5)
  timeLimit: number | null;   // vteřiny, null = bez limitu
  judgeUids: string[];
  config: StationConfigTarget | StationConfigGongsSequential | StationConfigGongsChoice;
  /** Dual mode: dvě sady gongů, dva střelci střílí najednou.
   *  Platí pouze pro gongs_sequential a gongs_choice. */
  dualShooter?: boolean;
}

// ── Rundy a střelci ───────────────────────────────────────────

export interface RoundShooter {
  startNumber: number;        // 101, 102 ... 601, 602
  userId: string;             // Firebase UID
  sequenceNumber: string;     // "043" – LKS id střelce
  name: string;               // "Jakub Vícha"
  active: boolean;            // false = DNS/odjel
  isNewbie?: boolean;         // nováček této sezóny
  newbieDisabled?: boolean;   // zakázáno systémem (má výsledky z min. roku)
}

export interface LiveRound {
  id: string;
  number: number;
  shooters: RoundShooter[];
}

// ── Výsledky – live data ──────────────────────────────────────

/** Výsledek stanoviště, jak ho předají gongy (čas a rozhodčího doplní zápis). */
export type JudgeShotsResult = Pick<StationResult, "score" | "shots" | "gongValues">;

export interface StationResult {
  score: number;
  /** 
   * Pro gongy sekvenční: 1 = zásah, 0 = miss (per gong)
   * Pro gongy výběr: bodová hodnota každé rány (0 = miss)
   * Pro terče: prázdné pole, score = celková suma
   */
  shots: number[];
  /** Snapshot hodnot gongů v době střelby (pro audit) */
  gongValues?: number[];
  completedAt: any;    // Firestore Timestamp
  enteredBy: string;   // UID rozhodčího
}

export interface LiveResult {
  startNumber: number;
  userId: string;
  sequenceNumber: string;
  name: string;
  roundNumber: number;
  totalScore: number;
  /** 0.01 = vítěz rozstřelu, 0/undefined = bez rozstřelu */
  tiebreaker?: number;
  updatedAt: any;      // Firestore Timestamp
  stations: { [stationId: string]: StationResult };
}

// ── Validace terče ────────────────────────────────────────────

/**
 * Zkontroluje, zda je zadaná suma dosažitelná pro daný terč a počet ran.
 * Používá DP přístup.
 */
export function isValidTargetScore(
  zones: number[],
  shotsCount: number,
  totalScore: number
): boolean {
  if (zones.length === 0 || shotsCount <= 0) return true;

  // Převeď vše na celá čísla (×10) aby se předešlo float problémům (30.1 → 301)
  const SCALE = 10;
  const scaledZones = [...zones.map(z => Math.round(z * SCALE)), 0]; // 0 = miss
  const scaledTarget = Math.round(totalScore * SCALE);

  const minPerShot = Math.min(...scaledZones);
  const maxPerShot = Math.max(...scaledZones);
  const minTotal = minPerShot * shotsCount;
  const maxTotal = maxPerShot * shotsCount;

  if (scaledTarget < minTotal || scaledTarget > maxTotal) return false;

  const size = maxTotal - minTotal + 1;
  // Pojistka pro případ extrémně velkých hodnot
  if (size > 100000) return true;

  const offset = -minTotal;
  let dp = new Array(size).fill(false);
  dp[0 + offset] = true;

  for (let shot = 0; shot < shotsCount; shot++) {
    const next = new Array(size).fill(false);
    for (let s = 0; s < size; s++) {
      if (!dp[s]) continue;
      for (const zone of scaledZones) {
        const ns = s + zone;
        if (ns >= 0 && ns < size) next[ns] = true;
      }
    }
    dp = next;
  }

  return dp[scaledTarget + offset] === true;
}
